import React, { useEffect, useRef, useState } from 'react'
import { fetchPublic } from '../ticketFetch'

const COMPLAINT_LIST = [
  'Vehicle Wiring issue',
  'Dock Wiring issue',
  'Vehicle motor/controller issue',
  'Vehicle Mechanical issues',
  'Dock not fixed',
  'Keyfob mapping',
  'Keyfob lost',
  'New retrofitting.',
  'Dock temperature issue',
  'DIU software issue',
  'DIU replaced',
  'Dock replaced',
  'DIU failed.',
  'Battery swapping issue',
  'Mobile number change',
  'Battery stuck in Dock',
  'Battery discharged',
  'Ignition switch issue',
  '7.5 A fuse issue',
  'Dock & BP Need to surrender',
]

const FSE_NAMES = [
  'Suraj Bhan',
  'Robin Kumar',
  'Ram Autar Agrahari',
  'Pawan Kumar',
  'Sumit Kushwaha',
  'Shivam Kushwaha',
  'Yogesh',
  'Ravi Pal',
  'Roshan Maurya',
  'Kapil',
  'Jayprakash Kushwaha',
  'Vikram Pundeer',
  'Pradeep Kumar Nayak',
  'Sonu Kumar',
  'Sandeep Kumar',
  'Anup Chaudhary',
]

const TICKET_STATUS_OPTIONS = [
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed',  label: 'Closed'  },
]

const emptyForm = (ticket, resolvedData) => {
  const rf = resolvedData?.resolution_fields || {}
  return {
    vin_number:        rf.vin_number        || ticket?.vehicle_details?.vin_number || '',
    dock_id:           rf.dock_id           || '',
    bp_id:             rf.bp_id             || '',
    problem_statement: rf.problem_statement || '',
    problem_found:     rf.problem_found     || '',
    action_taken:      rf.action_taken      || '',
    complaint_status:  rf.complaint_status  || '',
    attended_by:       rf.attended_by       || '',
    address:           rf.address           || '',
    city:              rf.city              || '',
    nearest_qis_id:    rf.nearest_qis_id    || '',
    ticket_status:     resolvedData?.ticket_status  || '',
    issue_resolved:    resolvedData?.issue_resolved || '',
  }
}

export default function DcoCloseForm({ ticket, ticketId, resolvedData, readOnly, onSubmit, submitting }) {
  const [form, setForm]             = useState(() => emptyForm(ticket, resolvedData))
  const [imageFiles, setImageFiles]     = useState([])          // { file, preview }[]
  const [existingImages, setExistingImages] = useState([])      // { url, name }[] from get-attachments
  const [imageLoading, setImageLoading]   = useState(false)
  const [imageUploading, setImageUploading] = useState(false)
  const [errors, setErrors]         = useState({})
  const fileInputRef                = useRef(null)

  const isClosed = form.ticket_status === 'closed'

  // Fetch existing picture_of_fault_found attachment
  useEffect(() => {
    if (!ticketId) return
    setImageLoading(true)
    fetchPublic(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=picture_of_fault_found`)
      .then((data) => {
        const list = Array.isArray(data) ? data : []
        setExistingImages(list.map((a) => ({ url: a.file, name: a.file_name?.split('/').pop() || 'fault-photo' })))
      })
      .catch(() => {})
      .finally(() => setImageLoading(false))
  }, [ticketId])

  function set(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'ticket_status' && value !== 'closed') next.issue_resolved = ''
      return next
    })
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  function handleImage(e) {
    const selected = Array.from(e.target.files || [])
    if (!selected.length) return
    const newEntries = selected.map((file) => ({ file, preview: URL.createObjectURL(file) }))
    setImageFiles((prev) => [...prev, ...newEntries])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function removeNewImage(index) {
    setImageFiles((prev) => prev.filter((_, i) => i !== index))
  }

  async function uploadImages(filesToUpload) {
    if (!filesToUpload.length || !ticketId) return
    setImageUploading(true)
    try {
      const fd = new FormData()
      filesToUpload.forEach(({ file }) => fd.append('files', file))
      await fetchPublic(
        `/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=picture_of_fault_found`,
        { method: 'POST', body: fd }
      )
      const uploaded = filesToUpload.map(({ file, preview }) => ({ url: preview, name: file.name }))
      setExistingImages((prev) => [...prev, ...uploaded])
      setImageFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch {
      setErrors((prev) => ({ ...prev, picture_of_fault: 'Upload failed. Please retry.' }))
      throw new Error('upload_failed')
    } finally {
      setImageUploading(false)
    }
  }

  function validate() {
    const e = {}
    if (!form.vin_number.trim())        e.vin_number        = 'Required'
    if (!form.problem_statement.trim()) e.problem_statement = 'Required'
    if (!form.problem_found)            e.problem_found     = 'Required'
    if (!form.action_taken.trim())      e.action_taken      = 'Required'
    if (!form.complaint_status)         e.complaint_status  = 'Required'
    if (!form.attended_by)              e.attended_by       = 'Required'
    if (!form.ticket_status)            e.ticket_status     = 'Required'
    if (isClosed && !form.issue_resolved) e.issue_resolved  = 'Required'
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    // Upload any pending files on submit
    if (imageFiles.length && ticketId) {
      try {
        await uploadImages(imageFiles)
      } catch {
        return
      }
    }

    const payload = {
      resolution_fields: {
        vin_number:        form.vin_number,
        dock_id:           form.dock_id,
        bp_id:             form.bp_id,
        problem_statement: form.problem_statement,
        problem_found:     form.problem_found,
        action_taken:      form.action_taken,
        complaint_status:  form.complaint_status,
        attended_by:       form.attended_by,
        address:           form.address,
        city:              form.city,
        nearest_qis_id:    form.nearest_qis_id,
      },
      ticket_status:   form.ticket_status,
      attachment_type: 'picture_of_fault_found',
      ...(isClosed && { issue_resolved: form.issue_resolved }),
    }
    onSubmit(payload)
  }

  return (
    <form className="ctf-section" onSubmit={handleSubmit} noValidate>

      {readOnly && (
        <div className="ctf-readonly-banner">
          &#128274; This ticket is closed — view only
        </div>
      )}

      {/* VIN No. */}
      <Field label="VIN No." required={!readOnly} error={errors.vin_number}>
        <input
          className="ctf-input"
          value={form.vin_number}
          onChange={(e) => set('vin_number', e.target.value)}
          placeholder="Enter VIN number"
          readOnly={readOnly}
        />
      </Field>

      {/* Dock ID */}
      <Field label="Dock ID" error={errors.dock_id}>
        <input
          className="ctf-input"
          value={form.dock_id}
          onChange={(e) => set('dock_id', e.target.value)}
          placeholder="Enter Dock ID"
          readOnly={readOnly}
        />
      </Field>

      {/* BP ID */}
      <Field label="BP ID" error={errors.bp_id}>
        <input
          className="ctf-input"
          value={form.bp_id}
          onChange={(e) => set('bp_id', e.target.value)}
          placeholder="Enter BP ID"
          readOnly={readOnly}
        />
      </Field>

      {/* Problem Statement */}
      <Field label="Problem Statement (Customer Voice)" required={!readOnly} error={errors.problem_statement}>
        <textarea
          className="ctf-textarea"
          value={form.problem_statement}
          onChange={(e) => set('problem_statement', e.target.value)}
          placeholder="Describe the problem in customer's words…"
          readOnly={readOnly}
        />
      </Field>

      {/* Problem Found */}
      <Field label="Problem Found" required={!readOnly} error={errors.problem_found}>
        <select
          className="ctf-select"
          value={form.problem_found}
          onChange={(e) => set('problem_found', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select complaint</option>
          {COMPLAINT_LIST.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </Field>

      {/* Picture of Fault Found */}
      <Field label="Picture of Fault Found" error={errors.picture_of_fault}>
        {imageLoading ? (
          <p className="ctf-hint">Loading…</p>
        ) : (
          <div className="ctf-image-preview-wrap">
            {/* Already uploaded images */}
            {existingImages.map((img, i) => (
              <div key={i} className="ctf-image-item">
                <img src={img.url} alt={img.name} className="ctf-image-preview" />
                <p className="ctf-hint">{img.name}</p>
              </div>
            ))}

            {/* Newly selected, not yet uploaded */}
            {imageFiles.map((entry, i) => (
              <div key={i} className="ctf-image-item">
                <img src={entry.preview} alt={`new-${i}`} className="ctf-image-preview" />
                <div className="ctf-image-actions">
                  <span className="ctf-hint">{entry.file.name}</span>
                  {!readOnly && (
                    <button type="button" className="ctf-image-remove" onClick={() => removeNewImage(i)}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Upload button — manual early upload */}
            {imageFiles.length > 0 && !readOnly && (
              <button
                type="button"
                className="ctf-upload-btn"
                onClick={() => uploadImages(imageFiles)}
                disabled={imageUploading}
              >
                {imageUploading ? 'Uploading…' : `&#8593; Upload ${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''}`}
              </button>
            )}

            {/* Add more / first upload picker */}
            {!readOnly && (
              <label className="ctf-file-label" style={existingImages.length || imageFiles.length ? { marginTop: 4 } : {}}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  className="ctf-file-input"
                  onChange={handleImage}
                />
                <span className="ctf-file-btn">
                  {existingImages.length || imageFiles.length ? '&#128247; Add more photos' : '&#128247; Upload photos'}
                </span>
              </label>
            )}

            {!existingImages.length && !imageFiles.length && readOnly && (
              <p className="ctf-hint">No photos uploaded</p>
            )}
          </div>
        )}
      </Field>

      {/* Action Taken */}
      <Field label="Action Taken" required={!readOnly} error={errors.action_taken}>
        <textarea
          className="ctf-textarea"
          value={form.action_taken}
          onChange={(e) => set('action_taken', e.target.value)}
          placeholder="Describe action taken…"
          readOnly={readOnly}
        />
      </Field>

      {/* Complaint Status */}
      <Field label="Complaint Status" required={!readOnly} error={errors.complaint_status}>
        <select
          className="ctf-select"
          value={form.complaint_status}
          onChange={(e) => set('complaint_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </Field>

      {/* Attended By */}
      <Field label="Attended By" required={!readOnly} error={errors.attended_by}>
        <select
          className="ctf-select"
          value={form.attended_by}
          onChange={(e) => set('attended_by', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select FSE</option>
          {FSE_NAMES.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </Field>

      {/* Address */}
      <Field label="Address" error={errors.address}>
        <input
          className="ctf-input"
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
          placeholder="Enter address"
          readOnly={readOnly}
        />
      </Field>

      {/* City */}
      <Field label="City" error={errors.city}>
        <input
          className="ctf-input"
          value={form.city}
          onChange={(e) => set('city', e.target.value)}
          placeholder="Enter city"
          readOnly={readOnly}
        />
      </Field>

      {/* Nearest QIS ID */}
      <Field label="Nearest QIS ID" error={errors.nearest_qis_id}>
        <input
          className="ctf-input"
          value={form.nearest_qis_id}
          onChange={(e) => set('nearest_qis_id', e.target.value)}
          placeholder="Enter QIS ID"
          readOnly={readOnly}
        />
      </Field>

      {/* ── Divider before status fields ── */}
      <div className="ctf-divider" />

      {/* Ticket Status */}
      <Field label="Ticket Status" required={!readOnly} error={errors.ticket_status}>
        <select
          className="ctf-select"
          value={form.ticket_status}
          onChange={(e) => set('ticket_status', e.target.value)}
          disabled={readOnly}
        >
          <option value="">Select status</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </Field>

      {/* Issue Resolved — only active when ticket_status is closed */}
      <Field
        label="Issue Resolved"
        required={isClosed && !readOnly}
        error={errors.issue_resolved}
        hint={!isClosed && !readOnly ? 'Available only when Ticket Status is Closed' : undefined}
      >
        <div className={`ctf-radio-group ${(!isClosed || readOnly) ? 'ctf-radio-group--disabled' : ''}`}>
          {['yes', 'no'].map((v) => (
            <label key={v} className="ctf-radio-label">
              <input
                type="radio"
                name="issue_resolved"
                value={v}
                checked={form.issue_resolved === v}
                disabled={!isClosed || readOnly}
                onChange={() => set('issue_resolved', v)}
                className="ctf-radio"
              />
              {v === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      </Field>

      {!readOnly && (
        <button type="submit" className="ctf-submit-btn" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Close Ticket'}
        </button>
      )}
    </form>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div className="ctf-field">
      <span className="ctf-label">
        {label}
        {required && <span className="ctf-required">*</span>}
      </span>
      {children}
      {hint  && !error && <span className="ctf-hint">{hint}</span>}
      {error && <span className="ctf-field-error">{error}</span>}
    </div>
  )
}
